import { useForm } from '@tanstack/react-form'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { roles } from '../constants/roles'
import { useAddUserMutation, useUpdateUserMutation } from '../hooks'
import { UserCreateSchema, UserUpdateSchema } from '../schemas/usersSchema'
import { useUsersStore } from '../store/usersStore'
import { UserRoleEnum } from '../types/user'
import type { UserRole, UserUpdateInput } from '../types/user'
import type { ApiError } from '@/types/api'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Modal } from '@/components/custom/modal'
import { FormInput } from '@/components/custom/form-input'
import LoadingButton from '@/components/custom/loading-button'
import { Switch } from '@/components/custom/switch'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { /* useEmailValidation,*/ useIsMobile } from '@/hooks'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { usePermissions } from '@/utils/permissions'
import { ChurchForm } from '@/features/churches/components/ChurchForm'
import { Button } from '@/components/ui/button'

const addDefaultValues = {
  email: '',
  name: '',
  roles: [] as Array<UserRole>,
  churchId: [],
  isAdminPastor: false,
}

const requireChurchIdRoles = ['PASTOR', 'TREASURER']

export function UserForm({
  id,
  type,
  trigger,
  open,
  onClose,
}: {
  id?: string | null
  type: 'add' | 'edit'
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}) {
  const isMobile = useIsMobile()
  const [openChurchDialog, setOpenChurchDialog] = useState(false)

  const { mutateAsync: addUserMutation, isPending: isAdding } =
    useAddUserMutation()
  const { mutateAsync: updateUserMutation, isPending: isUpdating } =
    useUpdateUserMutation()
  // const emailValidator = useEmailValidation()

  const users = useUsersStore((s) => s.users)
  const initial = users.find((u) => u._id === id)
  const churches = useChurchesStore((s) => s.churches)

  // Get permissions for church filtering
  const permissions = usePermissions()

  // Filter churches based on user's role and assignments
  const assignedChurchIds = permissions.getUserAssignedChurches()
  const filteredChurches =
    assignedChurchIds.length === 0
      ? churches // Super admin sees all churches
      : churches.filter((church) => assignedChurchIds.includes(church._id))
  const form = useForm({
    defaultValues:
      type === 'edit'
        ? {
            _id: initial?._id || '',
            email: initial?.email || '',
            name: initial?.name || '',
            roles: initial?.roles || [],
            churchId: initial?.churchId || [],
            active: initial?.active ?? false,
            isAdminPastor: initial?.isAdminPastor ?? false,
          }
        : addDefaultValues,
    onSubmit: async ({ value }) => {
      try {
        if (type === 'edit') {
          await updateUserMutation({
            ...value,
            churchId: requireChurchIdRoles.some((r) =>
              value.roles.includes(r as UserRole),
            )
              ? (value.churchId as Array<string>)
              : [],
          } as UserUpdateInput)
        } else {
          await addUserMutation({
            ...value,
            roles: value.roles,
            churchId: requireChurchIdRoles.some((r) =>
              value.roles.includes(r as UserRole),
            )
              ? (value.churchId as Array<string>)
              : [],
          })
        }
        onClose?.()
        form.reset()
      } catch (error) {
        console.log('Error occurred while submitting form:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting user form',
        )
      }
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
  }

  const schema = type === 'edit' ? UserUpdateSchema : UserCreateSchema

  const isPending = isAdding || isUpdating
  const buttonLabel = type === 'add' ? 'Add' : 'Update'
  const loadingLabel = type === 'add' ? 'Adding...' : 'Updating...'

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={type === 'edit' ? 'Edit User' : 'Add User'}
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      <form
        id="user-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2 h-full flex flex-col"
      >
        <div className="flex-1 overflow-y-auto space-y-2">
          <form.Field
            name="email"
            validators={
              {
                // onBlurAsync: async ({ value }) => {
                //   return await emailValidator({ value })
                // },
              }
            }
            children={(field) => {
              return (
                <div className="gap-0">
                  <FormInput
                    field={field}
                    label="Email"
                    type="email"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    required
                  />
                </div>
              )
            }}
          />

          <form.Field
            name="name"
            validators={{
              onBlur: ({ value }) => {
                const result = schema.shape.name.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => {
              return (
                <div className="gap-0">
                  <FormInput
                    field={field}
                    label="Name"
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    required
                  />
                </div>
              )
            }}
          />

          <form.Field
            name="roles"
            validators={{
              onBlur: ({ value }) => {
                const result = schema.shape.roles.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => {
              return (
                <div className="gap-0">
                  <AutoCompleteSelect
                    field={field}
                    label="Roles"
                    id={field.name}
                    name={field.name}
                    options={roles.map((role) => ({
                      value: role._id,
                      label: role.name,
                    }))}
                    multiple
                    required
                  />
                  {!isMobile && (
                    <div className="text-xs text-neutral-500 mt-1">
                      {roles.map((r) => (
                        <p
                          key={r._id}
                          className="mb-2 w-full block text-justify"
                        >
                          <span className="font-bold italic">{r.name}</span>:{' '}
                          {r.description}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )
            }}
          />

          <form.Subscribe
            selector={(state) => [state.values.roles]}
            children={([roleValue]) => {
              const shouldShowChurchField = requireChurchIdRoles.some((r) =>
                roleValue.includes(r as UserRole),
              )
              const shouldShowAdminPastorField = roleValue.includes(
                UserRoleEnum.PASTOR,
              )

              return (
                <div className="col-span-2">
                  {shouldShowChurchField && (
                    <form.Field
                      name="churchId"
                      validators={{
                        onBlur: ({ value }) => {
                          const result = schema.shape.churchId.safeParse(value)
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => {
                        return (
                          <div className="gap-0">
                            <AutoCompleteSelect
                              field={field}
                              label="Church"
                              id={field.name}
                              name={field.name}
                              options={filteredChurches.map((church) => ({
                                value: church._id,
                                label: church.name,
                              }))}
                              required
                              multiple
                              addDialog={
                                <ChurchForm
                                  type="add"
                                  onClose={() => setOpenChurchDialog(false)}
                                  open={openChurchDialog}
                                  trigger={
                                    <Button
                                      onClick={() => {
                                        setOpenChurchDialog(true)
                                      }}
                                    >
                                      <Plus /> Add Church
                                    </Button>
                                  }
                                />
                              }
                            />
                          </div>
                        )
                      }}
                    />
                  )}

                  {(shouldShowAdminPastorField || type === 'edit') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                      {shouldShowAdminPastorField && (
                        <form.Field
                          name="isAdminPastor"
                          validators={{
                            onBlur: ({ value }) => {
                              const result = schema.safeParse({
                                ...form.state.values,
                                isAdminPastor: value,
                              })
                              return result.success
                                ? undefined
                                : result.error.errors.find(
                                    (e) => e.path[0] === 'isAdminPastor',
                                  )?.message
                            },
                          }}
                          children={(field) => {
                            return (
                              <div className="gap-0">
                                <Switch
                                  field={field}
                                  label="Is Admin Pastor?"
                                  id={field.name}
                                  name={field.name}
                                />
                              </div>
                            )
                          }}
                        />
                      )}

                      {type === 'edit' && (
                        <form.Field
                          name="active"
                          validators={{
                            onBlur: ({ value }) => {
                              const result = schema.safeParse({
                                ...form.state.values,
                                active: value,
                              })
                              return result.success
                                ? undefined
                                : result.error.errors.find(
                                    (e) => e.path[0] === 'active',
                                  )?.message
                            },
                          }}
                          children={(field) => {
                            return (
                              <div className="gap-0">
                                <Switch
                                  field={field}
                                  label="Is user active?"
                                  id={field.name}
                                  name={field.name}
                                />
                              </div>
                            )
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )
            }}
          />
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit]}
          children={([canSubmit]) => (
            <div className="pt-4 border-t">
              <LoadingButton
                type="submit"
                className="w-full"
                isLoading={isPending}
                disabled={!canSubmit || isPending}
              >
                {isPending ? loadingLabel : buttonLabel}
              </LoadingButton>
            </div>
          )}
        />
      </form>
    </Modal>
  )
}
